// itemService - Item CRUD API client
// Implements US-3.1: Item CRUD and Drag-Drop Reordering

import apiClient from '../utils/apiClient';
import { getToken } from '../utils/tokenStorage';
import { Item } from '../types';

/**
 * API response structure
 */
interface ApiResponse<T> {
  success: boolean;
  error?: string;
  item?: T;
  itemId?: string;
}

/**
 * CREATE new item in workspace
 * POST /api/workspaces/:workspaceId/items
 *
 * @param workspaceId - Workspace ID
 * @param title - Item title (max 200 chars)
 * @returns Created item or error
 */
export async function createItem(workspaceId: string, title: string): Promise<ApiResponse<Item>> {
  try {
    const token = getToken();
    if (!token) {
      return { success: false, error: 'Not authenticated' };
    }

    const response = await apiClient.post(
      `/api/workspaces/${workspaceId}/items`,
      { title },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    return response.data;
  } catch (error: any) {
    return {
      success: false,
      error: error.response?.data?.error || 'Failed to create item',
    };
  }
}

/**
 * UPDATE item title or order
 * PATCH /api/workspaces/:workspaceId/items/:itemId
 *
 * @param workspaceId - Workspace ID
 * @param itemId - Item ID
 * @param updates - Fields to update (title and/or order)
 * @returns Updated item or error
 */
export async function updateItem(
  workspaceId: string,
  itemId: string,
  updates: { title?: string; order?: number }
): Promise<ApiResponse<Item>> {
  try {
    const token = getToken();
    if (!token) {
      return { success: false, error: 'Not authenticated' };
    }

    const response = await apiClient.patch(
      `/api/workspaces/${workspaceId}/items/${itemId}`,
      updates,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    return response.data;
  } catch (error: any) {
    return {
      success: false,
      error: error.response?.data?.error || 'Failed to update item',
    };
  }
}

/**
 * DELETE item
 * DELETE /api/workspaces/:workspaceId/items/:itemId
 *
 * @param workspaceId - Workspace ID
 * @param itemId - Item ID
 * @returns Success status or error
 */
export async function deleteItem(workspaceId: string, itemId: string): Promise<ApiResponse<never>> {
  try {
    const token = getToken();
    if (!token) {
      return { success: false, error: 'Not authenticated' };
    }

    const response = await apiClient.delete(
      `/api/workspaces/${workspaceId}/items/${itemId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    return response.data;
  } catch (error: any) {
    return {
      success: false,
      error: error.response?.data?.error || 'Failed to delete item',
    };
  }
}
